import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';

interface UseQuickQuestionsRotationOptions {
  minChipWidth?: number;
  dwellMs?: number;
  groupSize?: number;
  /** false 时一批恒为 groupSize 个(不随视口宽度自适应)。默认 true(web 行为)。 */
  adaptiveBatch?: boolean;
  /** 测量溢出用的 chip 文字外层选择器。默认 web 的 .dcs-web-qq-chip-text-wrap。 */
  wrapSelector?: string;
  /** wrap 内的文字节点选择器。默认 web 的 .dcs-web-qq-chip-text。 */
  textSelector?: string;
}

interface ChipMetric {
  distance: number;
  duration: number;
}

interface QuickQuestionLike {
  id: string;
  text: string;
}

type FlipPhase = 'idle' | 'out' | 'in';

const DEFAULT_MIN_CHIP_WIDTH = 150;
const DEFAULT_GROUP_SIZE = 2;
const DEFAULT_WRAP_SELECTOR = '.dcs-web-qq-chip-text-wrap';
const DEFAULT_TEXT_SELECTOR = '.dcs-web-qq-chip-text';
const DEFAULT_DWELL_MS = 4000;
const ITEM_PX_PER_SEC = 70;
const MIN_MARQUEE_DURATION_MS = 2500;
const OVERFLOW_THRESHOLD_PX = 2;
const MARQUEE_TAIL_PX = 12;
const MARQUEE_LEFT_SEGMENT_RATIO = 0.55;
const FLIP_OUT_MS = 220;
const FLIP_IN_MS = 220;

function chunkArr<T>(arr: T[], size: number): T[][] {
  if (size <= 0 || arr.length === 0) return [];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

function computeChipsPerBatch(viewportWidth: number, minChipWidth: number, groupSize: number): number {
  if (viewportWidth <= 0) return groupSize;
  const fits = Math.floor(viewportWidth / minChipWidth);
  const aligned = fits - (fits % groupSize);
  return Math.max(groupSize, aligned);
}

/**
 * 常见问题轮播 composable (V2)。
 *
 * - 视口宽度决定一批 chip 数, chip 等宽。
 * - chip 内文字超长 → CSS marquee 五段式 (开头停 → 慢左滚 → 末端停 → 快右滚 → 开头停),
 *   动画跑完自然停在文字开头。
 * - 切批次时启动 Y 轴翻牌 (整批 rotateY out → 换 batch → in), 翻牌期间不启动新 marquee。
 * - hover 任意 chip: 取消切批次 timer; 若 chip 是 marquee, 命令式重启该 chip 的 marquee
 *   (restartMarquee, 不重挂载 DOM)。
 * - mouseleave: restartToken++, 主 effect 重跑, 重新测 marquee + arm 完整时长的 timer。
 */
export function useQuickQuestionsRotation<T extends QuickQuestionLike>(
  items: T[],
  opts: UseQuickQuestionsRotationOptions = {},
) {
  const minChipWidth = opts.minChipWidth ?? DEFAULT_MIN_CHIP_WIDTH;
  const dwellMs = opts.dwellMs ?? DEFAULT_DWELL_MS;
  const groupSize = opts.groupSize ?? DEFAULT_GROUP_SIZE;
  const adaptiveBatch = opts.adaptiveBatch ?? true;
  const wrapSelector = opts.wrapSelector ?? DEFAULT_WRAP_SELECTOR;
  const textSelector = opts.textSelector ?? DEFAULT_TEXT_SELECTOR;

  const [viewportEl, setViewportEl] = useState<HTMLDivElement | null>(null);
  const viewportRef = useCallback((el: HTMLDivElement | null) => setViewportEl(el), []);

  const [viewportWidth, setViewportWidth] = useState(0);
  const [batchIndex, setBatchIndex] = useState(0);
  const [restartToken, setRestartToken] = useState(0);
  const [chipMetrics, setChipMetrics] = useState<Record<string, ChipMetric>>({});
  const [flipPhase, setFlipPhase] = useState<FlipPhase>('idle');

  const chipsPerBatch = useMemo(
    () =>
      adaptiveBatch
        ? computeChipsPerBatch(viewportWidth, minChipWidth, groupSize)
        : groupSize,
    [viewportWidth, minChipWidth, groupSize, adaptiveBatch],
  );

  const batches = useMemo(() => chunkArr(items, chipsPerBatch), [items, chipsPerBatch]);

  const timerRef = useRef<number | null>(null);
  const flipTimerRef = useRef<number[]>([]);
  const chipMetricsRef = useRef<Record<string, ChipMetric>>({});
  chipMetricsRef.current = chipMetrics;

  const cancelTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const cancelFlipTimers = () => {
    flipTimerRef.current.forEach((h) => clearTimeout(h));
    flipTimerRef.current = [];
  };

  useEffect(() => {
    setBatchIndex(0);
    setRestartToken(0);
    setChipMetrics({});
    setFlipPhase('idle');
    cancelTimer();
    cancelFlipTimers();
  }, [items, chipsPerBatch]);

  useEffect(() => {
    if (!viewportEl) return;
    if (typeof ResizeObserver === 'undefined') {
      setViewportWidth(viewportEl.clientWidth);
      return;
    }
    setViewportWidth(viewportEl.clientWidth);
    const ro = new ResizeObserver(() => {
      setViewportWidth(viewportEl.clientWidth);
    });
    ro.observe(viewportEl);
    return () => ro.disconnect();
  }, [viewportEl]);

  useEffect(() => {
    if (!viewportEl) return;
    if (batches.length === 0) return;
    if (flipPhase !== 'idle') return;

    setChipMetrics({});

    let cancelled = false;

    const raf = requestAnimationFrame(() => {
      if (cancelled) return;
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

      const wraps = viewportEl.querySelectorAll<HTMLElement>(wrapSelector);
      const nextMetrics: Record<string, ChipMetric> = {};
      let maxMarqueeDurationMs = 0;

      wraps.forEach((wrapEl) => {
        const chipId = wrapEl.dataset.chipId;
        if (!chipId) return;
        const textEl = wrapEl.querySelector<HTMLElement>(textSelector);
        if (!textEl) return;
        const overflow = textEl.scrollWidth - wrapEl.clientWidth;
        if (overflow > OVERFLOW_THRESHOLD_PX && !reduceMotion) {
          const distance = overflow + MARQUEE_TAIL_PX;
          const leftSegmentMs = (distance / ITEM_PX_PER_SEC) * 1000;
          const duration = Math.max(
            MIN_MARQUEE_DURATION_MS,
            leftSegmentMs / MARQUEE_LEFT_SEGMENT_RATIO,
          );
          nextMetrics[chipId] = { distance, duration };
          if (duration > maxMarqueeDurationMs) maxMarqueeDurationMs = duration;
        }
      });

      setChipMetrics(nextMetrics);

      const totalMs =
        maxMarqueeDurationMs > 0
          ? maxMarqueeDurationMs + dwellMs
          : batches.length > 1
            ? dwellMs
            : 0;

      if (totalMs > 0) {
        cancelTimer();
        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          if (batches.length > 1) {
            startFlip();
          } else {
            setRestartToken((t) => t + 1);
          }
        }, totalMs);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      cancelTimer();
    };
    // startFlip 内部用最新 batches.length, 用 ref 跨闭包不必要 — deps 已涵盖
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchIndex, restartToken, batches, dwellMs, viewportEl, flipPhase]);

  const startFlip = () => {
    cancelFlipTimers();
    setFlipPhase('out');
    const h1 = window.setTimeout(() => {
      setBatchIndex((i) => (batches.length > 0 ? (i + 1) % batches.length : 0));
      setFlipPhase('in');
      const h2 = window.setTimeout(() => {
        setFlipPhase('idle');
      }, FLIP_IN_MS);
      flipTimerRef.current.push(h2);
    }, FLIP_OUT_MS);
    flipTimerRef.current = [h1];
  };

  useEffect(
    () => () => {
      cancelTimer();
      cancelFlipTimers();
    },
    [],
  );

  /**
   * 命令式重启指定 chip 的跑马灯动画。
   * 不能用「改 key 重挂载组件」来重启:重挂载会让静止指针下方的新节点被浏览器
   * hover 重评估后重发 mouseenter,形成「移入 → 重挂载 → 再移入」死循环,
   * mousedown/mouseup 之间按钮节点已被换掉,click 丢失 → 长文案 chip 点不动。
   */
  const restartMarquee = (chipId: string) => {
    if (!viewportEl) return;
    const wraps = viewportEl.querySelectorAll<HTMLElement>(wrapSelector);
    wraps.forEach((wrapEl) => {
      if (wrapEl.dataset.chipId !== chipId) return;
      const textEl = wrapEl.querySelector<HTMLElement>(textSelector);
      if (!textEl) return;
      textEl.style.animation = 'none';
      void textEl.offsetWidth; // 强制 reflow:否则 none → 清空 被浏览器合并,动画不重启
      textEl.style.animation = '';
    });
  };

  const onChipHoverEnter = (chipId: string) => {
    cancelTimer();
    if (chipMetricsRef.current[chipId]) {
      restartMarquee(chipId);
    }
  };

  const onChipHoverLeave = () => {
    setRestartToken((t) => t + 1);
  };

  const currentBatch = batches[batchIndex] ?? [];

  return {
    currentBatch,
    chipMetrics,
    flipPhase,
    onChipHoverEnter,
    onChipHoverLeave,
    viewportRef,
  };
}
